import { useEffect, useState } from 'react'
import { useDelay, useRequest, useView } from '@10000/react'
import { SplashScreen, useBackground, useShell } from '../../layout'
import { Body, Box, FinishQuestionDialog, H3, H4, Icon, Meter, ParticipantsDialog } from '../../widgets'
import { questions } from '../../../lookups'
import { SquizButton, SquizScreen } from '../common'
import './Consensus.scss'

export const Consensus = ({ active }) => {
  const request = useRequest('squiz')
  const { presenter, questionIndex, question, teamSize, consensusAnswers, playersAnswered, consensusLockedIn, options } = useView('squiz')
  const { presenterParticipates, kiosk, questionCount, solo } = options || {}

  const [participantsOpen, setParticipantsOpen] = useState(false)
  const [finishDialogOpen, setFinishDialogOpen] = useState(false)

  const splashComplete = useDelay(3000)
  const lockedIn = !!consensusLockedIn
  const squizQuestion = questions[question]?.consensus || questions[question]
  const playing = presenterParticipates || !presenter || kiosk
  const longQuestion = squizQuestion?.question.length > 80

  const meter = consensusAnswers?.[0]?.answer
  const [answer, setAnswer] = useState(consensusAnswers?.[0]?.answer)

  useEffect(() => {
    setAnswer(consensusAnswers?.[0]?.answer)
  }, [consensusAnswers])

  useShell({ active, mode: 'squiz narrow left-aligned', step: questionIndex + 1, max: questionCount })

  useBackground().set(mobile => mobile
    ? { active, background: undefined }
    : { active, background: 1, position: 'right -300px bottom -500px', size: '1000px' }
  )

  const viewResults = () => {
    request('consensusComplete')
    setFinishDialogOpen(false)
  }

  const handleViewResults = () => {
    if(playersAnswered !== teamSize) {
      setFinishDialogOpen(true)
    } else {
      viewResults()
    }
  }
  const handleMeterRelease = (answer, isMouseUp) => {
    setAnswer(answer)
    if(isMouseUp && playing) {
      return request('participantAnsweredConsensus', { answer })
    }
  }
  const handleLockedIn = () => request('participantLockedIn')
  const handleUnlocked = () => request('participantLockedIn', { unlock: true })

  return <>
    <SquizScreen participants={presenter}>
      <Box
        container
        column
        fullWidth
        fullHeight
        gap={32}
        className="question-container"
        justifyContent="space-between"
      >
        <Box
          column
          gap={32}
          height={200}
          opacity={(!lockedIn || !playing) ? 1 : 0}
          transition={0.5}
        >
          <H4 align="center" className={longQuestion ? 'long-question' : undefined}>{squizQuestion?.question}</H4>
          <Body align="center" className={(answer !== null && answer !== undefined) ? undefined : 'blink'}>
            {(answer !== null && answer !== undefined) ? squizQuestion?.answers[Math.round(answer)] : 'Touch to start'}
          </Body>
        </Box>

        <Box
          column
          fullWidth
          gap={8}
          maxWidth={600}
          position="absolute"
          top={lockedIn ? 40 : 'min(50%, calc(100% - min(calc((100vw - 64px) / 2), 400px))'}
          zIndex={1}
          transition={0.5}
        >
          <Meter value={meter} onChange={handleMeterRelease} answers={consensusAnswers} disabled={(lockedIn && !presenter)}/>

          <Box fullWidth justifyContent="space-between" alignItems="center">
            <Box alignItems="center" justifyContent="center">
              <Icon icon="Team" size={16}/>
              <Body lineHeight="150%" size={12}>{playersAnswered}/{teamSize} RESPONDED</Body>
            </Box>
            
            {(presenter && !solo) &&
              <Box className="mobile-only" alignItems="center" onClick={() => setParticipantsOpen(true)}>
                <Body bold underline alt italic>Manage Users</Body>
              </Box>
            }
          </Box>
        </Box>

        <Box
          column
          position="absolute"
          inset
          justifyContent="space-between"
          opacity={(lockedIn && playing) ? 1 : 0}
          transition={0.5}
          pb={80}
        >
          <Box height="min(300px, calc(100vw / 2))" />

          <H3 align="center">Locked in</H3>
          {presenter && <Body align="center" className="blink">Touch meter to change</Body>}
          <H4
            opacity={0}
            align="center"
            className={playersAnswered !== teamSize ? 'blink' : undefined}
          >
            Waiting for others
          </H4>
        </Box>

        <Box fullWidth zIndex={2}>
          {(((!lockedIn && presenter) || !presenter) && playing) &&
            <SquizButton
              onClick={lockedIn ? handleUnlocked : handleLockedIn}
              disabled={meter === null}
              text={lockedIn ? 'Tap to unlock' : 'Lock it in'}
              hideTools={!presenter}
            />
          }
          {(presenter && (!playing || lockedIn)) &&
            <SquizButton
              onClick={handleViewResults}
              text="Reveal results"
            />
          }
        </Box>
      </Box>

      {presenter &&
        <ParticipantsDialog open={participantsOpen} onClose={() => setParticipantsOpen(false)} />
      }
    </SquizScreen>

    <SplashScreen
      open={!splashComplete && !presenter}
      background="var(--black-fade), var(--red)"
      p={32}
      zIndex={10}
    >
      <H3 align="center">Consensus mode</H3>
    </SplashScreen>

    <FinishQuestionDialog
      open={finishDialogOpen}
      onClose={() => setFinishDialogOpen(false)}
      onContinue={viewResults}
      showControls={false}
    />
  </>
}